/**
 * LinkInBio — dashboard for the user's zlink.club/{slug} pages.
 *
 * Multi-page. Two top-level components:
 *   1. <LinkInBio />        — list view at /dashboard/link-in-bio. Shows all
 *                              pages the user owns, plan quota indicator, and
 *                              a "create new" card. With 0 pages it auto-shows
 *                              the slug-claim flow inline.
 *   2. <LinkInBioEditor />  — editor for one page at /dashboard/link-in-bio/:id.
 *                              Loads page + links by id and reuses the same
 *                              section components (Profile, Design, …).
 *
 * Plan limits: enforced server-side via /api/bio/quota and 403 PLAN_LIMIT
 * responses from POST /api/bio/pages.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  AtSign, Check, ChevronDown, ChevronUp, ExternalLink, Eye, EyeOff,
  Globe, GripVertical, Loader2, Pencil, Plus, Save, Trash2, AlertCircle,
  Palette, Sparkles, Mail, BarChart3, Search, Download, MousePointer2,
  Upload, User, X, ArrowLeft, Copy,
  type LucideProps,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type BioPage = {
  id: string;
  userId: string;
  slug: string;
  title: string;
  bio: string;
  avatarMediaId: string | null;
  /** Resolved from media.url server-side so the dashboard can render it directly. */
  avatarUrl: string | null;
  theme: 'light' | 'dark' | 'brand';
  brandColor: string | null;
  status: 'draft' | 'published';
  views: number;

  // Custom design
  buttonStyle: 'filled' | 'outline' | 'soft' | 'pill';
  buttonColor: string | null;
  buttonTextColor: string | null;
  backgroundType: 'theme' | 'solid' | 'gradient';
  backgroundValue: string | null;
  textColor: string | null;
  fontFamily: string | null;

  // Featured banner
  featuredEnabled: boolean;
  featuredTitle: string | null;
  featuredSubtitle: string | null;
  featuredImageUrl: string | null;
  featuredUrl: string | null;

  // Email capture
  emailCaptureEnabled: boolean;
  emailCaptureTitle: string;
  emailCaptureButton: string;
  emailCaptureSuccess: string;
  mailchimpApiKey: string | null;
  mailchimpAudienceId: string | null;

  // Custom SEO
  seoTitle: string | null;
  seoDescription: string | null;
  seoImageUrl: string | null;

  // Analytics
  gaMeasurementId: string | null;
  metaPixelId: string | null;
  tiktokPixelId: string | null;

  // Social media icons row (key = network id, value = URL)
  socials: Record<string, string>;
};

type BioAnalytics = {
  views: number;
  totalClicks: number;
  ctr: number;
  subscribers: number;
  links: Array<{ id: string; title: string; url: string; clicks: number; active: boolean }>;
};

type BioSubscriber = {
  id: string;
  email: string;
  source: string;
  createdAt: string;
  syncedToMailchimpAt: string | null;
};

type PatchablePage = Partial<Omit<BioPage, 'id' | 'userId' | 'slug' | 'views'>>;

type BioLink = {
  id: string;
  title: string;
  url: string;
  iconType: 'emoji' | 'lucide' | 'social';
  iconValue: string;
  sortOrder: number;
  active: boolean;
  clicks: number;
};

type BioResponse = { page: BioPage | null; links: BioLink[] };

const ZLINK_DOMAIN = 'zlink.club';

/* ── List view (default route) ─────────────────────────── */

type BioPageListItem = {
  id: string;
  slug: string;
  title: string;
  bio: string;
  status: 'draft' | 'published';
  views: number;
  totalClicks: number;
  avatarMediaId: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

type BioQuota = { limit: number | null; current: number };

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-32 text-slate-400">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
}

export function LinkInBio() {
  useEffect(() => {
    document.title = 'Link in bio · Zettapost';
  }, []);

  const { data: pagesData, isLoading } = useQuery({
    queryKey: ['bio', 'pages'],
    queryFn: () => api<{ pages: BioPageListItem[] }>('/api/bio/pages'),
  });
  const { data: quota } = useQuery({
    queryKey: ['bio', 'quota'],
    queryFn: () => api<BioQuota>('/api/bio/quota'),
  });

  if (isLoading) return <LoadingSpinner />;

  const pages = pagesData?.pages ?? [];
  const q = quota ?? { limit: 0, current: pages.length };
  const canCreate = q.limit === null || q.current < q.limit;

  // First-time experience: no pages → show the claim flow inline.
  if (pages.length === 0) {
    return <ClaimSlug canCreate={canCreate} quota={q} />;
  }

  return <PagesList pages={pages} quota={q} canCreate={canCreate} />;
}

/** Single-line subtitle with the plan quota. */
function QuotaLine({ quota }: { quota: BioQuota }) {
  const txt =
    quota.limit === null
      ? `${quota.current} página${quota.current === 1 ? '' : 's'} · ilimitadas en tu plan`
      : `${quota.current} de ${quota.limit} página${quota.limit === 1 ? '' : 's'} usadas`;
  return <span className="text-sm text-slate-500">{txt}</span>;
}

function PagesList({
  pages,
  quota,
  canCreate,
}: {
  pages: BioPageListItem[];
  quota: BioQuota;
  canCreate: boolean;
}) {
  const [showClaim, setShowClaim] = useState(false);
  const [analyticsPageId, setAnalyticsPageId] = useState<string | null>(null);
  const analyticsPage = pages.find((p) => p.id === analyticsPageId) ?? null;

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <AtSign size={22} className="text-brand-600" /> Link in bio
          </h1>
          <p className="mt-1">
            <QuotaLine quota={quota} />
          </p>
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={() => setShowClaim(true)}
            className="btn-primary inline-flex items-center gap-1.5"
          >
            <Plus size={14} /> Nueva página
          </button>
        ) : (
          <RouterLink
            to="/dashboard/billing"
            className="btn-secondary inline-flex items-center gap-1.5"
            title="Sube de plan para crear más páginas"
          >
            Sube de plan →
          </RouterLink>
        )}
      </div>

      {!canCreate && quota.limit !== null && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          Has alcanzado el límite de tu plan ({quota.limit} página
          {quota.limit === 1 ? '' : 's'}).{' '}
          <RouterLink to="/dashboard/billing" className="font-semibold underline">
            Mejora tu plan
          </RouterLink>{' '}
          para crear más.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pages.map((p) => (
          <PageCard key={p.id} page={p} onOpenAnalytics={setAnalyticsPageId} />
        ))}
      </div>

      {showClaim && <ClaimSlugDialog onClose={() => setShowClaim(false)} />}
      {analyticsPage && (
        <AnalyticsModal
          page={analyticsPage}
          onClose={() => setAnalyticsPageId(null)}
        />
      )}
    </>
  );
}

/* ── Analytics modal (opened from a page card) ─────────── */

function AnalyticsModal({
  page,
  onClose,
}: {
  page: BioPageListItem;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['bio', 'analytics', page.id],
    queryFn: () => api<BioAnalytics>(`/api/bio/pages/${page.id}/analytics`),
    staleTime: 30_000,
  });

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          {page.avatarUrl ? (
            <img
              src={page.avatarUrl}
              alt=""
              className="h-10 w-10 rounded-full object-cover border border-slate-200"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <User size={16} />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-900 text-sm truncate">
              {page.title || `@${page.slug}`}
            </h2>
            <p className="text-[11px] text-slate-500 truncate">zlink.club/{page.slug}</p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-slate-400">
            <Loader2 size={18} className="animate-spin inline" />
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Visitas" value={data.views} loading={false} />
              <StatCard label="Clics totales" value={data.totalClicks} loading={false} />
              <StatCard
                label="CTR"
                value={`${(data.ctr * 100).toFixed(1)}%`}
                loading={false}
              />
              <StatCard label="Suscriptores" value={data.subscribers} loading={false} />
            </div>

            {data.links.length > 0 ? (
              <div className="mt-5">
                <h3 className="text-xs font-semibold text-slate-900 mb-2">Clics por enlace</h3>
                <ul className="space-y-1.5">
                  {data.links.map((l) => {
                    const max = Math.max(1, ...data.links.map((x) => x.clicks));
                    const pct = (l.clicks / max) * 100;
                    return (
                      <li key={l.id} className="text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-slate-700">{l.title}</span>
                          <span className="font-semibold text-slate-900 shrink-0">{l.clicks}</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              l.active ? 'bg-brand-500' : 'bg-slate-300',
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <p className="mt-5 text-xs text-slate-400 text-center">
                Aún no hay enlaces en esta página.
              </p>
            )}

            <div className="mt-5 pt-4 border-t border-slate-100 flex justify-between items-center">
              <RouterLink
                to={`/dashboard/link-in-bio/${page.id}`}
                className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
              >
                <Pencil size={11} /> Editar página
              </RouterLink>
              <a
                href={`https://${ZLINK_DOMAIN}/${page.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              >
                <ExternalLink size={11} /> Ver pública
              </a>
            </div>
          </>
        ) : (
          <p className="text-xs text-rose-600 text-center py-6">
            No se pudieron cargar las analíticas.
          </p>
        )}
      </div>
    </div>
  );
}

function PageCard({
  page,
  onOpenAnalytics,
}: {
  page: BioPageListItem;
  onOpenAnalytics: (id: string) => void;
}) {
  return (
    <RouterLink
      to={`/dashboard/link-in-bio/${page.id}`}
      className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-brand-300 hover:shadow-md transition"
    >
      <div className="flex items-center gap-3">
        {page.avatarUrl ? (
          <img
            src={page.avatarUrl}
            alt=""
            className="h-12 w-12 rounded-full object-cover border border-slate-200"
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <User size={20} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">
            {page.title || `@${page.slug}`}
          </p>
          <p className="text-xs text-slate-500 truncate">zlink.club/{page.slug}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium',
            page.status === 'published'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-slate-100 text-slate-600',
          )}
        >
          {page.status === 'published' ? (
            <>
              <Eye size={11} /> Publicada
            </>
          ) : (
            <>
              <EyeOff size={11} /> Borrador
            </>
          )}
        </span>
        <button
          type="button"
          onClick={(e) => {
            // Don't follow the surrounding RouterLink.
            e.preventDefault();
            e.stopPropagation();
            onOpenAnalytics(page.id);
          }}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-md px-1.5 py-0.5 transition"
          title="Ver analítica"
        >
          <span className="inline-flex items-center gap-1">
            <Eye size={11} /> {page.views}
          </span>
          <span className="inline-flex items-center gap-1">
            <MousePointer2 size={11} /> {page.totalClicks}
          </span>
        </button>
      </div>
    </RouterLink>
  );
}

/* ── Editor route ──────────────────────────────────────── */

export function LinkInBioEditor() {
  const { pageId } = useParams<{ pageId: string }>();
  useEffect(() => {
    document.title = 'Link in bio · Zettapost';
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bio', 'page', pageId],
    queryFn: () => api<BioResponse>(`/api/bio/pages/${pageId}`),
    enabled: !!pageId,
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError || !data?.page) {
    return (
      <div className="py-24 text-center">
        <p className="text-slate-500 mb-4">No encontramos esta página.</p>
        <RouterLink to="/dashboard/link-in-bio" className="btn-secondary inline-flex">
          <ArrowLeft size={14} /> Volver al listado
        </RouterLink>
      </div>
    );
  }
  return <Editor page={data.page} links={data.links} />;
}

/* ── Header ────────────────────────────────────────────── */

function PageHeader({ subtitle }: { subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        <AtSign size={22} className="text-brand-600" /> Link in bio
      </h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
  );
}

/* ── Claim flow ────────────────────────────────────────── */

/** Compact dialog version used from the list view. */
function ClaimSlugDialog({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
        <ClaimSlugInner onCreated={onClose} variant="dialog" />
      </div>
    </div>
  );
}

/** Full-page version used when the user has zero pages. */
function ClaimSlug({
  canCreate,
  quota,
}: {
  canCreate: boolean;
  quota: BioQuota;
}) {
  if (!canCreate) {
    return (
      <>
        <PageHeader subtitle="Necesitas un plan activo para crear tu Link in bio." />
        <div className="max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <p className="text-sm text-amber-900">
            {quota.limit === 0
              ? 'Necesitas una suscripción activa para crear tu primer Link in bio.'
              : `Has alcanzado el límite de ${quota.limit} página(s) de tu plan.`}
          </p>
          <RouterLink to="/dashboard/billing" className="btn-primary mt-4 inline-flex">
            Ir a planes
          </RouterLink>
        </div>
      </>
    );
  }
  return (
    <>
      <PageHeader subtitle="Crea tu página de enlaces con un dominio propio en zlink.club." />
      <div className="max-w-xl">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <ClaimSlugInner variant="page" />
        </div>
      </div>
    </>
  );
}

function ClaimSlugInner({
  onCreated,
  variant,
}: {
  onCreated?: () => void;
  variant: 'page' | 'dialog';
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<{ available: boolean; reason?: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setCheck(null);
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await api<{ available: boolean; reason?: string }>(
          `/api/bio/slug-check?slug=${encodeURIComponent(slug)}`,
        );
        setCheck(res);
      } catch {
        setCheck({ available: false, reason: 'Error comprobando disponibilidad.' });
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [slug]);

  const claim = useMutation({
    mutationFn: () =>
      api<BioResponse>('/api/bio/pages', {
        method: 'POST',
        body: JSON.stringify({ slug }),
      }),
    onSuccess: (res) => {
      // Invalidate the list + quota; pre-seed the per-page cache.
      qc.invalidateQueries({ queryKey: ['bio', 'pages'] });
      qc.invalidateQueries({ queryKey: ['bio', 'quota'] });
      if (res.page) {
        qc.setQueryData(['bio', 'page', res.page.id], res);
        navigate(`/dashboard/link-in-bio/${res.page.id}`);
      }
      onCreated?.();
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiError ? err.message : 'No se pudo reservar el nombre.');
    },
  });

  const canSubmit = Boolean(slug) && Boolean(check?.available) && !checking && !claim.isPending;

  return (
    <>
      {variant === 'dialog' && (
        <div className="flex items-start gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Globe size={18} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Nueva página en zlink.club</h2>
            <p className="mt-1 text-sm text-slate-600">
              Elige el nombre de usuario público de esta página.
            </p>
          </div>
        </div>
      )}
      <ClaimSlugForm
        slug={slug}
        setSlug={setSlug}
        check={check}
        checking={checking}
        submitError={submitError}
        canSubmit={canSubmit}
        onSubmit={() => claim.mutate()}
        submitting={claim.isPending}
        showIntro={variant === 'page'}
        onSlugChange={() => setSubmitError(null)}
      />
    </>
  );
}

function ClaimSlugForm({
  slug,
  setSlug,
  check,
  checking,
  submitError,
  canSubmit,
  onSubmit,
  submitting,
  showIntro,
  onSlugChange,
}: {
  slug: string;
  setSlug: (s: string) => void;
  check: { available: boolean; reason?: string } | null;
  checking: boolean;
  submitError: string | null;
  canSubmit: boolean;
  onSubmit: () => void;
  submitting: boolean;
  showIntro: boolean;
  onSlugChange: () => void;
}) {
  return (
    <>
      {showIntro && (
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Globe size={18} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Reserva tu Link in bio</h2>
            <p className="mt-1 text-sm text-slate-600">
              Elige tu nombre de usuario público — luego podrás compartirlo en
              tu bio de Instagram, TikTok, X, etc.
            </p>
          </div>
        </div>
      )}
      <label className={cn('block', showIntro && 'mt-5')}>
        <span className="text-xs font-medium text-slate-700">Tu URL pública</span>
        <div className="mt-1.5 flex items-stretch rounded-lg border border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100 overflow-hidden">
          <span className="px-3 inline-flex items-center bg-slate-50 text-slate-500 text-sm border-r border-slate-300 select-none">
            {ZLINK_DOMAIN}/
          </span>
          <input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''));
              onSlugChange();
            }}
            placeholder="tu-nombre"
            className="flex-1 px-3 py-2.5 text-sm bg-white outline-none placeholder:text-slate-400"
            maxLength={30}
            autoFocus
          />
          <span className="px-3 inline-flex items-center text-slate-400 text-sm">
            {checking ? (
              <Loader2 size={14} className="animate-spin" />
            ) : check?.available ? (
              <Check size={14} className="text-emerald-600" />
            ) : check && !check.available ? (
              <AlertCircle size={14} className="text-rose-500" />
            ) : null}
          </span>
        </div>
        {check && !check.available && check.reason && (
          <p className="mt-1.5 text-xs text-rose-600">{check.reason}</p>
        )}
        {check?.available && (
          <p className="mt-1.5 text-xs text-emerald-600">Disponible ✨</p>
        )}
        {submitError && <p className="mt-1.5 text-xs text-rose-600">{submitError}</p>}
      </label>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={onSubmit}
        className="mt-5 btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Reservando…
          </>
        ) : (
          <>Reservar nombre</>
        )}
      </button>

      <p className="mt-3 text-[11px] text-slate-400">
        Letras, números, guion y guion bajo. Entre 3 y 30 caracteres.
      </p>
    </>
  );
}

/* ── Save status indicator ─────────────────────────────── */

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function SaveIndicator({ status, lastSaved }: { status: SaveStatus; lastSaved: Date | null }) {
  const [, tick] = useState(0);

  // Re-render every 10s so the "hace Xs" label stays current.
  useEffect(() => {
    if (status !== 'saved') return;
    const id = setInterval(() => tick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, [status]);

  if (status === 'saving') {
    return (
      <span className="text-xs text-slate-400 inline-flex items-center gap-1.5">
        <Loader2 size={12} className="animate-spin" /> Guardando…
      </span>
    );
  }
  if (status === 'saved' && lastSaved) {
    const secs = Math.round((Date.now() - lastSaved.getTime()) / 1000);
    const label = secs < 60 ? `hace ${secs}s` : `hace ${Math.round(secs / 60)}min`;
    return (
      <span className="text-xs text-emerald-600 inline-flex items-center gap-1.5">
        <Check size={12} /> Guardado {label}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="text-xs text-rose-500 inline-flex items-center gap-1.5">
        <AlertCircle size={12} /> Error al guardar
      </span>
    );
  }
  return null;
}

/* ── Editor ────────────────────────────────────────────── */

function Editor({ page, links }: { page: BioPage; links: BioLink[] }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(page.title);
  const [bio, setBio] = useState(page.bio);
  const [status, setStatus] = useState(page.status);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const publicUrl = `https://${ZLINK_DOMAIN}/${page.slug}`;

  const savePage = useMutation({
    mutationFn: (patch: PatchablePage) =>
      api<{ page: BioPage }>(`/api/bio/pages/${page.id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: (res) => {
      qc.setQueryData<BioResponse>(['bio', 'page', page.id], (prev) =>
        prev ? { ...prev, page: res.page } : prev,
      );
      // The list view also shows title/avatar/status — invalidate it.
      qc.invalidateQueries({ queryKey: ['bio', 'pages'] });
      setLastSaved(new Date());
      setSaveStatus('saved');
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  // Auto-save title/bio after 600ms of inactivity.
  useEffect(() => {
    if (title === page.title && bio === page.bio) return;
    const t = setTimeout(() => {
      savePage.mutate({ title, bio });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, bio]);

  useEffect(() => {
    if (status === page.status) return;
    savePage.mutate({ status });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const sortedLinks = useMemo(
    () => [...links].sort((a, b) => a.sortOrder - b.sortOrder),
    [links],
  );

  return (
    <>
      <RouterLink
        to="/dashboard/link-in-bio"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-600 mb-3"
      >
        <ArrowLeft size={12} /> Todas mis páginas
      </RouterLink>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <AtSign size={22} className="text-brand-600" /> Link in bio
          </h1>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600"
          >
            {ZLINK_DOMAIN}/{page.slug}
            <ExternalLink size={13} />
          </a>
        </div>
        <div className="flex items-center gap-2">
          <SaveIndicator status={saveStatus} lastSaved={lastSaved} />
          <CopyLinkButton url={publicUrl} />
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition"
            title="Abrir página pública"
          >
            <ExternalLink size={13} /> Ver
          </a>
          <button
            type="button"
            onClick={() => setStatus(status === 'published' ? 'draft' : 'published')}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition',
              status === 'published'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
            )}
            title={status === 'published' ? 'Despublicar' : 'Publicar'}
          >
            {status === 'published' ? (
              <>
                <Eye size={13} /> Publicada
              </>
            ) : (
              <>
                <EyeOff size={13} /> Borrador
              </>
            )}
          </button>
          <DeletePageButton pageId={page.id} slug={page.slug} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Editor column */}
        <div className="space-y-6">
          {/* Profile card */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Perfil</h2>

            <AvatarUploader page={page} onSave={(p) => savePage.mutate(p)} />

            <label className="mt-5 block">
              <span className="text-xs font-medium text-slate-700">Título</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Tu nombre o el de tu marca"
                maxLength={80}
                className="mt-1.5 w-full px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
              />
            </label>
            <label className="mt-4 block">
              <span className="text-xs font-medium text-slate-700">Bio</span>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Cuenta en una frase a qué te dedicas."
                maxLength={200}
                rows={2}
                className="mt-1.5 w-full px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none resize-none"
              />
              <span className="mt-1 inline-block text-[10px] text-slate-400">
                {bio.length}/200
              </span>
            </label>

            <div className="mt-4">
              <ThemePresetPicker page={page} onSave={(p) => savePage.mutate(p)} />
            </div>
          </section>

          <SocialsSection page={page} onSave={(p) => savePage.mutate(p)} />
          <DesignSection page={page} onSave={(p) => savePage.mutate(p)} />
          <FeaturedSection page={page} onSave={(p) => savePage.mutate(p)} />

          {/* Links */}
          <LinksSection links={sortedLinks} pageId={page.id} />

          <CaptureSection page={page} onSave={(p) => savePage.mutate(p)} />
          <SeoSection page={page} onSave={(p) => savePage.mutate(p)} />
          <AnalyticsSection page={page} onSave={(p) => savePage.mutate(p)} />
        </div>

        {/* Preview column */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <Preview
            page={{ ...page, title, bio }}
            links={sortedLinks.filter((l) => l.active)}
          />
        </div>
      </div>
    </>
  );
}

/* ── Copy link button ──────────────────────────────────── */

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers / non-secure contexts.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition',
        copied
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
      )}
      title={copied ? '¡Copiado!' : 'Copiar enlace público'}
      aria-label="Copiar enlace"
    >
      {copied ? (
        <>
          <Check size={13} /> Copiado
        </>
      ) : (
        <>
          <Copy size={13} /> Copiar
        </>
      )}
    </button>
  );
}

/* ── Delete page button ────────────────────────────────── */

function DeletePageButton({ pageId, slug }: { pageId: string; slug: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const del = useMutation({
    mutationFn: () => api<{ ok: true }>(`/api/bio/pages/${pageId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bio', 'pages'] });
      qc.invalidateQueries({ queryKey: ['bio', 'quota'] });
      qc.removeQueries({ queryKey: ['bio', 'page', pageId] });
      navigate('/dashboard/link-in-bio');
    },
  });

  return (
    <button
      type="button"
      disabled={del.isPending}
      onClick={() => {
        const ok = confirm(
          `¿Eliminar la página zlink.club/${slug}? Esta acción no se puede deshacer y elimina también sus enlaces y suscriptores.`,
        );
        if (ok) del.mutate();
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition disabled:opacity-50"
      title="Eliminar esta página"
      aria-label="Eliminar página"
    >
      {del.isPending ? (
        <Loader2 size={13} className="animate-spin" />
      ) : (
        <Trash2 size={13} />
      )}
    </button>
  );
}

/* ── Shared editor section helper ─────────────────────── */

function EditorSection({
  icon: Icon,
  title,
  description,
  defaultOpen = false,
  badge,
  children,
}: {
  icon: ComponentType<LucideProps>;
  title: string;
  description?: string;
  defaultOpen?: boolean;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <details
      className="group rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      open={defaultOpen}
    >
      <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
            {badge}
          </div>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
        <ChevronDown
          size={16}
          className="text-slate-400 transition-transform group-open:rotate-180 shrink-0"
        />
      </summary>
      <div className="px-5 pb-5 border-t border-slate-100 pt-4">{children}</div>
    </details>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <span
        className={cn(
          'inline-flex h-5 w-9 items-center rounded-full px-0.5 transition',
          checked ? 'bg-brand-600' : 'bg-slate-200',
        )}
        onClick={() => onChange(!checked)}
      >
        <span
          className={cn(
            'h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </span>
      <span className="text-xs font-medium text-slate-700">{label}</span>
    </label>
  );
}

/* ── Avatar uploader ───────────────────────────────────── */

const AVATAR_MAX_MB = 5;
const AVATAR_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

function AvatarUploader({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = `avatar-upload-${page.id}`;

  async function handleFile(file: File) {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Sube una imagen (PNG, JPG, WEBP).');
      return;
    }
    if (file.size > AVATAR_MAX_MB * 1024 * 1024) {
      setError(`La imagen supera ${AVATAR_MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      // 1. Ask the server for a presigned upload URL.
      const presign = await api<{
        id: string;
        uploadUrl: string;
        headers: Record<string, string>;
      }>('/api/media/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          mimeType: file.type,
          sizeBytes: file.size,
          originalName: file.name,
        }),
      });

      // 2. PUT the file directly to S3.
      const put = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: presign.headers,
        body: file,
      });
      if (!put.ok) throw new Error('Upload failed');

      // 3. Confirm the upload (marks media row as ready).
      await api('/api/media/confirm', {
        method: 'POST',
        body: JSON.stringify({ id: presign.id }),
      });

      // 4. Bind the media id to this bio page.
      onSave({ avatarMediaId: presign.id });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  }

  function clear() {
    setError(null);
    onSave({ avatarMediaId: null });
  }

  return (
    <div>
      <span className="text-xs font-medium text-slate-700">Foto de perfil</span>
      <div className="mt-2 flex items-center gap-4">
        <div className="relative">
          {page.avatarUrl ? (
            <img
              src={page.avatarUrl}
              alt="Tu avatar"
              className="h-20 w-20 rounded-full object-cover border border-slate-200"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
              <User size={28} />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-brand-600" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={inputId}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition',
              uploading
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50',
            )}
          >
            <Upload size={12} />
            {page.avatarUrl ? 'Cambiar foto' : 'Subir foto'}
          </label>
          <input
            id={inputId}
            type="file"
            accept={AVATAR_ACCEPT}
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = ''; // allow re-selecting the same file
            }}
            className="hidden"
          />
          {page.avatarUrl && !uploading && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-rose-600"
            >
              <X size={11} /> Quitar
            </button>
          )}
          <p className="text-[10px] text-slate-400">
            PNG, JPG o WEBP · máx. {AVATAR_MAX_MB} MB · cuadrada queda mejor
          </p>
        </div>
      </div>
      {error && (
        <p className="mt-2 text-xs text-rose-600 inline-flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

/* ── Social media row ──────────────────────────────────── */

type SocialNetwork = {
  id: string;
  label: string;
  placeholder: string;
  /** Inline SVG markup, 24×24 viewBox. */
  svg: string;
};

/** Same canonical list the server uses (kept in sync manually). */
const SOCIAL_NETWORKS: SocialNetwork[] = [
  { id: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/tu-usuario',
    svg: '<path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>' },
  { id: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/tu-usuario',
    svg: '<path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>' },
  { id: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@tu-usuario',
    svg: '<path fill="currentColor" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.6a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84z"/>' },
  { id: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@tu-canal',
    svg: '<path fill="currentColor" d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/>' },
  { id: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/tu-pagina',
    svg: '<path fill="currentColor" d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06c0 5.01 3.66 9.16 8.44 9.91v-7.01H7.9v-2.9h2.54v-2.2c0-2.51 1.49-3.9 3.77-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.77l-.44 2.9h-2.33V22c4.78-.75 8.44-4.9 8.44-9.94z"/>' },
  { id: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/tu-perfil',
    svg: '<path fill="currentColor" d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .78 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .78 23.2 0 22.22 0z"/>' },
  { id: 'threads', label: 'Threads', placeholder: 'https://threads.net/@tu-usuario',
    svg: '<path fill="currentColor" d="M12.18 24h-.06c-3.33-.02-5.9-1.13-7.62-3.28C3 18.79 2.07 16.13 2.03 12.83v-.16C2.07 9.37 3 6.71 4.5 4.79 6.22 2.64 8.79 1.53 12.12 1.5h.05c2.71.02 4.95.78 6.66 2.26 1.62 1.4 2.65 3.35 3.04 5.78l-2.39.39c-.71-4.04-3.42-5.51-7.31-5.54-2.59.02-4.55.83-5.83 2.42-1.2 1.49-1.83 3.61-1.86 6.33.03 2.72.66 4.85 1.86 6.33 1.28 1.59 3.24 2.4 5.83 2.42 2.34-.02 3.88-.6 5.16-1.93.91-.94 1.41-2.21 1.4-3.62v-.02c.01-.55-.07-1.05-.23-1.49-.43-1.18-1.46-1.99-2.86-2.21l-.21-.04c-.6-.1-1.27-.13-1.94-.07-.86.08-1.45.4-1.78.94-.31.5-.34 1.18-.07 1.71.27.51.77.83 1.51.85.08 0 .16 0 .25-.01l.23 2.34c-.18.03-.37.04-.55.04-1.7-.04-2.86-.88-3.51-2.18-.71-1.4-.61-3.11.26-4.5.93-1.48 2.61-2.32 5-2.52 1.16-.1 2.31-.04 3.36.18 1.46.31 2.69.97 3.55 1.95 1.21 1.38 1.61 3.13 1.04 4.6-.43 1.16-1.29 2.17-2.42 2.93-1.25.84-2.83 1.34-4.43 1.42z"/>' },
  { id: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/tu-usuario',
    svg: '<path fill="currentColor" d="M12 0C5.4 0 0 5.4 0 12c0 5.1 3.1 9.4 7.6 11.2-.1-.95-.2-2.43.04-3.47.22-.94 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.66.96-2.9 2.16-2.9 1.02 0 1.51.77 1.51 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.18 1.78 2.18 2.14 0 3.78-2.26 3.78-5.51 0-2.88-2.07-4.9-5.03-4.9-3.43 0-5.44 2.57-5.44 5.23 0 1.04.4 2.15.9 2.75.1.12.11.22.08.34l-.34 1.4c-.05.22-.17.27-.4.16-1.5-.7-2.43-2.89-2.43-4.65 0-3.78 2.75-7.26 7.93-7.26 4.16 0 7.4 2.97 7.4 6.93 0 4.14-2.61 7.47-6.24 7.47-1.22 0-2.36-.63-2.75-1.38 0 0-.6 2.3-.75 2.86-.27 1.04-1 2.35-1.49 3.15A12 12 0 0 0 24 12c0-6.6-5.4-12-12-12z"/>' },
  { id: 'bluesky', label: 'Bluesky', placeholder: 'https://bsky.app/profile/tu-usuario',
    svg: '<path fill="currentColor" d="M12 10.8C10.85 8.56 7.69 4.4 4.76 2.33 1.95.33 0 .6 0 2.5c0 1.74 1.45 9.95 1.74 11.18.46 1.95 2.46 2.65 4.27 2.27 1.05-.22 1.43-.34 1.92-.46-2.5.51-3.94 2.34-2.84 5 .89 2.15 4.42 5.84 6.91 1.51 2.49 4.33 6.02.64 6.91-1.5 1.1-2.67-.34-4.5-2.84-5 .49.13.87.25 1.92.47 1.81.38 3.81-.33 4.27-2.28C22.55 12.45 24 4.24 24 2.5c0-1.9-1.95-2.17-4.76-.17-2.93 2.07-6.09 6.23-7.24 8.47z"/>' },
  { id: 'github', label: 'GitHub', placeholder: 'https://github.com/tu-usuario',
    svg: '<path fill="currentColor" d="M12 .3a12 12 0 0 0-3.8 23.38c.6.12.83-.26.83-.58V21.1c-3.33.72-4.04-1.6-4.04-1.6-.55-1.4-1.34-1.77-1.34-1.77-1.08-.74.08-.73.08-.73 1.2.09 1.83 1.24 1.83 1.24 1.08 1.83 2.81 1.3 3.5.99.1-.78.42-1.31.76-1.61-2.66-.3-5.46-1.33-5.46-5.93 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.52.12-3.18 0 0 1-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.66.25 2.88.12 3.18a4.65 4.65 0 0 1 1.23 3.22c0 4.61-2.8 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .3z"/>' },
  { id: 'twitch', label: 'Twitch', placeholder: 'https://twitch.tv/tu-usuario',
    svg: '<path fill="currentColor" d="M11.57 4.93h1.71v5.14h-1.71V4.93zm4.71 0H18v5.14h-1.71V4.93zM6 0L1.71 4.29v15.42h5.14V24l4.29-4.29h3.43L22.29 12V0H6zm14.57 11.14l-3.43 3.43h-3.43l-3 3v-3H6.86V1.71h13.71v9.43z"/>' },
  { id: 'spotify', label: 'Spotify', placeholder: 'https://open.spotify.com/artist/…',
    svg: '<path fill="currentColor" d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.51 17.32a.75.75 0 0 1-1.03.25c-2.83-1.73-6.38-2.12-10.58-1.17a.75.75 0 1 1-.34-1.46c4.59-1.04 8.52-.6 11.7 1.35.36.22.47.69.25 1.03zm1.47-3.27a.94.94 0 0 1-1.28.3c-3.24-2-8.19-2.58-12.03-1.42a.94.94 0 1 1-.55-1.8c4.4-1.34 9.86-.7 13.55 1.6.43.27.58.84.31 1.32zm.13-3.4c-3.88-2.3-10.3-2.51-14-1.4a1.12 1.12 0 1 1-.65-2.14c4.26-1.3 11.34-1.05 15.81 1.6a1.12 1.12 0 0 1-1.15 1.94z"/>' },
  { id: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/34123456789',
    svg: '<path fill="currentColor" d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.28-.1-.47-.15-.67.15s-.78.97-.95 1.17c-.18.2-.36.22-.66.07-.3-.15-1.26-.46-2.4-1.48-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.6.13-.13.3-.34.45-.51.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.66-1.6-.92-2.18-.24-.57-.49-.5-.66-.5h-.57c-.2 0-.5.07-.78.37-.27.3-1.04 1.01-1.04 2.47s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.07 4.49.7.3 1.26.48 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.34zM12 .5C5.83.5.8 5.53.8 11.7c0 2.07.54 4.04 1.57 5.8L.5 23.5l6.16-1.6a11.16 11.16 0 0 0 5.33 1.36h.01c6.16 0 11.2-5.03 11.2-11.2C23.2 5.93 18.16.5 12 .5zm0 20.5c-1.7 0-3.36-.45-4.8-1.3l-.35-.2-3.65.95.97-3.55-.23-.36a9.16 9.16 0 0 1-1.4-4.84c0-5.06 4.13-9.18 9.2-9.18 5.06 0 9.18 4.12 9.18 9.18 0 5.07-4.12 9.2-9.18 9.2z"/>' },
  { id: 'telegram', label: 'Telegram', placeholder: 'https://t.me/tu-usuario',
    svg: '<path fill="currentColor" d="M11.94.5C5.6.5.45 5.65.45 12s5.15 11.5 11.5 11.5c6.35 0 11.5-5.15 11.5-11.5S18.3.5 11.94.5zm5.7 7.85l-1.92 9.05c-.14.65-.52.81-1.06.5l-2.93-2.16-1.42 1.36c-.16.16-.29.29-.59.29l.21-2.98 5.41-4.88c.23-.21-.05-.32-.36-.11l-6.7 4.21-2.88-.9c-.63-.2-.64-.63.13-.93l11.27-4.35c.52-.19.98.13.81.93z"/>' },
  { id: 'email', label: 'Email', placeholder: 'mailto:tu@email.com',
    svg: '<path fill="currentColor" d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"/>' },
  { id: 'website', label: 'Sitio web', placeholder: 'https://tu-web.com',
    svg: '<path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm7.94 9h-2.92c-.13-1.7-.56-3.32-1.23-4.74A8.04 8.04 0 0 1 19.94 11zM12 4.04c.94 1.4 1.62 3.16 1.85 4.96h-3.7c.23-1.8.91-3.56 1.85-4.96zM4.06 13h2.92c.13 1.7.56 3.32 1.23 4.74A8.04 8.04 0 0 1 4.06 13zm0-2a8.04 8.04 0 0 1 4.15-4.74C7.54 7.68 7.11 9.3 6.98 11H4.06zm7.94 8.96c-.94-1.4-1.62-3.16-1.85-4.96h3.7c-.23 1.8-.91 3.56-1.85 4.96zM14.13 13H9.87c-.07-.66-.07-1.34 0-2h4.26c.07.66.07 1.34 0 2zm1.7 4.74c.67-1.42 1.1-3.04 1.23-4.74h2.92a8.04 8.04 0 0 1-4.15 4.74z"/>' },
];

function SocialIcon({ svg, size = 18 }: { svg: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      // The svg paths come from a fixed allowlist defined above (not user input)
      // so dangerouslySetInnerHTML here is safe.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function SocialsSection({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  // Local mirror of the socials map so each keystroke updates the preview
  // immediately; we flush to the backend after 600ms of inactivity.
  const [local, setLocal] = useState<Record<string, string>>(page.socials ?? {});

  // Re-sync when the underlying page changes (e.g. preset switch).
  useEffect(() => {
    setLocal(page.socials ?? {});
  }, [page.socials]);

  useEffect(() => {
    const id = setTimeout(() => {
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(local)) {
        const t = v.trim();
        if (t) cleaned[k] = t;
      }
      // Skip the save if the cleaned map is identical to what's already on the page.
      const same =
        Object.keys(cleaned).length === Object.keys(page.socials ?? {}).length &&
        Object.entries(cleaned).every(([k, v]) => page.socials?.[k] === v);
      if (!same) onSave({ socials: cleaned });
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  const activeCount = Object.values(local).filter((v) => v.trim().length > 0).length;

  return (
    <EditorSection
      icon={AtSign}
      title="Redes sociales"
      description="Iconos clicables debajo de tu bio. Solo se muestran los que rellenes."
      badge={
        activeCount > 0 ? (
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold">
            {activeCount}
          </span>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-2.5">
        {SOCIAL_NETWORKS.map((net) => (
          <div
            key={net.id}
            className="flex items-stretch rounded-lg border border-slate-200 overflow-hidden focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100"
          >
            <div className="w-10 flex items-center justify-center bg-slate-50 text-slate-500 border-r border-slate-200">
              <SocialIcon svg={net.svg} size={16} />
            </div>
            <div className="flex-1 flex flex-col">
              <span className="px-3 pt-1 text-[10px] font-semibold text-slate-500">
                {net.label}
              </span>
              <input
                value={local[net.id] ?? ''}
                onChange={(e) => setLocal({ ...local, [net.id]: e.target.value })}
                placeholder={net.placeholder}
                className="px-3 pb-1.5 text-sm bg-white outline-none placeholder:text-slate-300"
              />
            </div>
            {local[net.id] && (
              <button
                type="button"
                onClick={() => {
                  const next = { ...local };
                  delete next[net.id];
                  setLocal(next);
                }}
                className="px-3 text-slate-400 hover:text-rose-600"
                title="Quitar"
                aria-label={`Quitar ${net.label}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </EditorSection>
  );
}

/* ── Custom Design ─────────────────────────────────────── */

const FONT_PRESETS: Array<{ id: string; label: string; sample: string }> = [
  { id: 'system', label: 'Sistema', sample: 'font-sans' },
  { id: 'inter', label: 'Inter', sample: 'font-sans' },
  { id: 'serif', label: 'Serif', sample: 'font-serif' },
  { id: 'rounded', label: 'Redondeada', sample: 'font-sans' },
  { id: 'mono', label: 'Mono', sample: 'font-mono' },
];

const GRADIENT_PRESETS: Array<{ id: string; label: string; value: string }> = [
  { id: 'sunset', label: 'Sunset', value: 'linear-gradient(135deg,#ff6a88 0%,#ffb86c 100%)' },
  { id: 'ocean', label: 'Océano', value: 'linear-gradient(135deg,#016fff 0%,#7c3aed 100%)' },
  { id: 'mint', label: 'Menta', value: 'linear-gradient(135deg,#10b981 0%,#06b6d4 100%)' },
  { id: 'peach', label: 'Melocotón', value: 'linear-gradient(135deg,#fbcfe8 0%,#fde68a 100%)' },
  { id: 'noir', label: 'Noir', value: 'linear-gradient(135deg,#1e293b 0%,#475569 100%)' },
  { id: 'lavender', label: 'Lavanda', value: 'linear-gradient(135deg,#a78bfa 0%,#f0abfc 100%)' },
];

/** Quick-pick palette shown above every hex input. */
const COLOR_SWATCHES: string[] = [
  '#016fff', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#8b5cf6', '#1e293b',
  '#64748b', '#ffffff', '#000000', '#fafafa',
];

/**
 * Pre-built design themes. Clicking one applies every design field at once
 * so beginners can get a polished look without touching individual knobs.
 */
type ThemePreset = {
  id: string;
  label: string;
  /** Tiny swatch shown in the picker. */
  preview: { bg: string; btn: string; text: string };
  patch: PatchablePage;
};

const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'minimal-light',
    label: 'Minimalista',
    preview: { bg: '#fafafa', btn: '#0f172a', text: '#0f172a' },
    patch: {
      theme: 'light',
      buttonStyle: 'filled',
      buttonColor: '#0f172a',
      buttonTextColor: '#ffffff',
      backgroundType: 'solid',
      backgroundValue: '#fafafa',
      textColor: '#0f172a',
      fontFamily: 'inter',
    },
  },
  {
    id: 'midnight',
    label: 'Medianoche',
    preview: { bg: '#0b1220', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'dark',
      buttonStyle: 'soft',
      buttonColor: '#ffffff',
      buttonTextColor: '#0b1220',
      backgroundType: 'solid',
      backgroundValue: '#0b1220',
      textColor: '#ffffff',
      fontFamily: 'inter',
    },
  },
  {
    id: 'sunset',
    label: 'Sunset',
    preview: { bg: 'linear-gradient(135deg,#ff6a88,#ffb86c)', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'light',
      buttonStyle: 'pill',
      buttonColor: '#ffffff',
      buttonTextColor: '#b91c1c',
      backgroundType: 'gradient',
      backgroundValue: 'linear-gradient(135deg,#ff6a88 0%,#ffb86c 100%)',
      textColor: '#ffffff',
      fontFamily: 'rounded',
    },
  },
  {
    id: 'ocean',
    label: 'Océano',
    preview: { bg: 'linear-gradient(135deg,#016fff,#7c3aed)', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'light',
      buttonStyle: 'pill',
      buttonColor: '#ffffff',
      buttonTextColor: '#1e3a8a',
      backgroundType: 'gradient',
      backgroundValue: 'linear-gradient(135deg,#016fff 0%,#7c3aed 100%)',
      textColor: '#ffffff',
      fontFamily: 'inter',
    },
  },
  {
    id: 'mint',
    label: 'Menta',
    preview: { bg: 'linear-gradient(135deg,#10b981,#06b6d4)', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'light',
      buttonStyle: 'soft',
      buttonColor: '#ffffff',
      buttonTextColor: '#065f46',
      backgroundType: 'gradient',
      backgroundValue: 'linear-gradient(135deg,#10b981 0%,#06b6d4 100%)',
      textColor: '#ffffff',
      fontFamily: 'rounded',
    },
  },
  {
    id: 'noir',
    label: 'Noir',
    preview: { bg: '#000000', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'dark',
      buttonStyle: 'outline',
      buttonColor: '#ffffff',
      buttonTextColor: '#000000',
      backgroundType: 'solid',
      backgroundValue: '#000000',
      textColor: '#ffffff',
      fontFamily: 'mono',
    },
  },
  {
    id: 'lavender',
    label: 'Lavanda',
    preview: { bg: 'linear-gradient(135deg,#a78bfa,#f0abfc)', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'light',
      buttonStyle: 'pill',
      buttonColor: '#ffffff',
      buttonTextColor: '#6d28d9',
      backgroundType: 'gradient',
      backgroundValue: 'linear-gradient(135deg,#a78bfa 0%,#f0abfc 100%)',
      textColor: '#ffffff',
      fontFamily: 'serif',
    },
  },
  {
    id: 'paper',
    label: 'Papel',
    preview: { bg: '#fef9f0', btn: '#7c2d12', text: '#1c1917' },
    patch: {
      theme: 'light',
      buttonStyle: 'outline',
      buttonColor: '#7c2d12',
      buttonTextColor: '#7c2d12',
      backgroundType: 'solid',
      backgroundValue: '#fef9f0',
      textColor: '#1c1917',
      fontFamily: 'serif',
    },
  },
  {
    id: 'forest',
    label: 'Bosque',
    preview: { bg: '#022c22', btn: '#34d399', text: '#ecfdf5' },
    patch: {
      theme: 'dark',
      buttonStyle: 'filled',
      buttonColor: '#34d399',
      buttonTextColor: '#022c22',
      backgroundType: 'solid',
      backgroundValue: '#022c22',
      textColor: '#ecfdf5',
      fontFamily: 'rounded',
    },
  },
  {
    id: 'neon',
    label: 'Neón',
    preview: { bg: '#020617', btn: '#22d3ee', text: '#a5f3fc' },
    patch: {
      theme: 'dark',
      buttonStyle: 'soft',
      buttonColor: '#22d3ee',
      buttonTextColor: '#020617',
      backgroundType: 'solid',
      backgroundValue: '#020617',
      textColor: '#e0f2fe',
      fontFamily: 'mono',
    },
  },
  {
    id: 'rose',
    label: 'Rosa',
    preview: { bg: '#fff1f2', btn: '#e11d48', text: '#881337' },
    patch: {
      theme: 'light',
      buttonStyle: 'pill',
      buttonColor: '#e11d48',
      buttonTextColor: '#ffffff',
      backgroundType: 'solid',
      backgroundValue: '#fff1f2',
      textColor: '#881337',
      fontFamily: 'serif',
    },
  },
  {
    id: 'aurora',
    label: 'Aurora',
    preview: { bg: 'linear-gradient(135deg,#22d3ee,#a78bfa,#f472b6)', btn: '#ffffff', text: '#ffffff' },
    patch: {
      theme: 'light',
      buttonStyle: 'pill',
      buttonColor: '#ffffff',
      buttonTextColor: '#1e293b',
      backgroundType: 'gradient',
      backgroundValue: 'linear-gradient(135deg,#22d3ee 0%,#a78bfa 50%,#f472b6 100%)',
      textColor: '#ffffff',
      fontFamily: 'rounded',
    },
  },
  {
    id: 'sand',
    label: 'Arena',
    preview: { bg: '#fef3c7', btn: '#92400e', text: '#451a03' },
    patch: {
      theme: 'light',
      buttonStyle: 'filled',
      buttonColor: '#92400e',
      buttonTextColor: '#fef3c7',
      backgroundType: 'solid',
      backgroundValue: '#fef3c7',
      textColor: '#451a03',
      fontFamily: 'serif',
    },
  },
];

/**
 * Theme preset picker — reusable card grid. Lives in the Profile section
 * (top-level) so beginners can change everything in one click; the more
 * granular controls below in "Diseño personalizado" let them fine-tune.
 */
/**
 * Build a `ThemePreset` from a saved Brand (Tu marca → Brand kit).
 * Uses the brand's first two colors: deepest as background, lightest accent
 * as button — with HSP luminance picking the right text color on top.
 */
type BrandLite = {
  id: string;
  name: string;
  logoUrl: string | null;
  colors: string[];
};

function brandPreset(brand: BrandLite): ThemePreset | null {
  const colors = (brand.colors ?? []).filter((c) => HEX_RE.test(c));
  if (colors.length === 0) return null;
  // Sort by luminance: darkest first.
  const sorted = [...colors].sort((a, b) => {
    return (isDarkHex(a) ? 0 : 1) - (isDarkHex(b) ? 0 : 1);
  });
  const bg = sorted[0]!;
  const btn = sorted[1] ?? (isDarkHex(bg) ? '#ffffff' : '#0f172a');
  const text = isDarkHex(bg) ? '#ffffff' : '#0f172a';
  const btnText = autoContrastingText(btn);
  return {
    id: `brand:${brand.id}`,
    label: `Mi marca · ${brand.name}`,
    preview: { bg, btn, text },
    patch: {
      theme: 'brand',
      brandColor: btn,
      buttonStyle: 'filled',
      buttonColor: btn,
      buttonTextColor: btnText,
      backgroundType: 'solid',
      backgroundValue: bg,
      textColor: text,
      fontFamily: 'inter',
    },
  };
}

function ThemePresetPicker({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  // Pull the user's brands so we can offer one-click "Mi marca" presets.
  const brandsQuery = useQuery({
    queryKey: ['brands', 'lite-for-bio'],
    queryFn: () => api<{ brands: BrandLite[] }>('/api/brands'),
    staleTime: 60_000,
  });
  const brandPresets: ThemePreset[] = (brandsQuery.data?.brands ?? [])
    .map(brandPreset)
    .filter((p): p is ThemePreset => p !== null);
  const allPresets = [...brandPresets, ...THEME_PRESETS];

  function isActive(p: ThemePreset): boolean {
    const k = p.patch;
    return (
      page.buttonStyle === k.buttonStyle &&
      (page.buttonColor ?? null) === (k.buttonColor ?? null) &&
      (page.buttonTextColor ?? null) === (k.buttonTextColor ?? null) &&
      page.backgroundType === k.backgroundType &&
      (page.backgroundValue ?? null) === (k.backgroundValue ?? null) &&
      (page.textColor ?? null) === (k.textColor ?? null) &&
      (page.fontFamily ?? null) === (k.fontFamily ?? null)
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-700">Tema</span>
        <span className="text-[10px] text-slate-400">1 clic = todo configurado</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {allPresets.map((p) => {
          const active = isActive(p);
          const isBrand = p.id.startsWith('brand:');
          // Pick a readable label color for the inner "Botón" pill — its text
          // color must contrast with the pill background, not the page bg.
          const btnTextOnPill = HEX_RE.test(p.preview.btn)
            ? autoContrastingText(p.preview.btn)
            : '#0f172a';
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSave(p.patch)}
              className={cn(
                'group relative rounded-xl border overflow-hidden transition focus:outline-none',
                active
                  ? 'border-brand-500 ring-2 ring-brand-200'
                  : 'border-slate-200 hover:border-slate-300',
              )}
              title={p.label}
            >
              <div
                className="h-14 w-full flex flex-col items-center justify-center gap-1"
                style={{ background: p.preview.bg, color: p.preview.text }}
              >
                <span className="text-[10px] font-semibold" style={{ color: p.preview.text }}>
                  Aa
                </span>
                <span
                  className="px-2 py-0.5 rounded-full text-[8px] font-semibold"
                  style={{
                    background: p.preview.btn,
                    color: btnTextOnPill,
                  }}
                >
                  Botón
                </span>
              </div>
              <div className="bg-white px-2 py-1.5 text-[11px] font-medium text-slate-700 text-center truncate">
                {p.label}
              </div>
              {isBrand && (
                <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 shadow">
                  <Palette size={8} />
                  Marca
                </span>
              )}
              {active && (
                <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-brand-500 text-white flex items-center justify-center">
                  <Check size={10} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {brandPresets.length === 0 && (
        <p className="mt-2 text-[10px] text-slate-400">
          ¿Quieres un tema con tus colores de marca? Configúralos en{' '}
          <RouterLink to="/dashboard/brand" className="underline hover:text-brand-600">
            Tu marca
          </RouterLink>{' '}
          y aparecerá aquí automáticamente.
        </p>
      )}
      <p className="mt-2 text-[10px] text-slate-400">
        Personaliza colores, fondo y tipografía en la sección "Diseño personalizado".
      </p>
    </div>
  );
}

function DesignSection({ page, onSave }: { page: BioPage; onSave: (p: PatchablePage) => void }) {
  return (
    <EditorSection
      icon={Palette}
      title="Diseño personalizado"
      description="Ajusta colores, fondo y tipografía manualmente."
    >
      {/* Button style */}
      <div>
        <span className="text-xs font-medium text-slate-700">Estilo de botón</span>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {(['filled', 'outline', 'soft', 'pill'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSave({ buttonStyle: s })}
              className={cn(
                'rounded-lg border px-2 py-2 text-[11px] font-medium transition',
                page.buttonStyle === s
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {s === 'filled'
                ? 'Sólido'
                : s === 'outline'
                  ? 'Contorno'
                  : s === 'soft'
                    ? 'Suave'
                    : 'Píldora'}
            </button>
          ))}
        </div>
      </div>

      {/* Button colors */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <ColorField
          label="Color de botón"
          value={page.buttonColor}
          onChange={(v) => onSave({ buttonColor: v })}
        />
        <ColorField
          label="Color de texto del botón"
          value={page.buttonTextColor}
          onChange={(v) => onSave({ buttonTextColor: v })}
        />
      </div>

      {/* Background */}
      <div className="mt-4">
        <span className="text-xs font-medium text-slate-700">Fondo</span>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {(['theme', 'solid', 'gradient'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => onSave({ backgroundType: b })}
              className={cn(
                'rounded-lg border px-2 py-2 text-[11px] font-medium transition',
                page.backgroundType === b
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {b === 'theme' ? 'Tema' : b === 'solid' ? 'Sólido' : 'Gradiente'}
            </button>
          ))}
        </div>

        {page.backgroundType === 'solid' && (
          <div className="mt-3">
            <ColorField
              label="Color de fondo"
              value={page.backgroundValue}
              onChange={(v) => onSave({ backgroundValue: v })}
            />
          </div>
        )}
        {page.backgroundType === 'gradient' && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {GRADIENT_PRESETS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => onSave({ backgroundValue: g.value })}
                style={{ background: g.value }}
                className={cn(
                  'h-12 rounded-lg border text-[10px] font-semibold text-white relative overflow-hidden transition',
                  page.backgroundValue === g.value
                    ? 'border-brand-500 ring-2 ring-brand-200'
                    : 'border-slate-200 hover:ring-2 hover:ring-brand-100',
                )}
              >
                <span className="absolute inset-0 flex items-end justify-center px-1 py-1 bg-gradient-to-t from-black/40 to-transparent">
                  {g.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Text color */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <ColorField
          label="Color de texto"
          value={page.textColor}
          onChange={(v) => onSave({ textColor: v })}
        />
      </div>

      {/* Font */}
      <div className="mt-4">
        <span className="text-xs font-medium text-slate-700">Tipografía</span>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {FONT_PRESETS.map((f) => {
            const active = (page.fontFamily ?? 'system') === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onSave({ fontFamily: f.id })}
                className={cn(
                  'rounded-lg border px-1 py-2 text-[10px] font-medium transition',
                  f.sample,
                  active
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                Aa
                <div className="mt-0.5">{f.label}</div>
              </button>
            );
          })}
        </div>
      </div>
    </EditorSection>
  );
}

/** Hex color picker with quick-pick swatches. Empty = reset to default. */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  const valid = !local || /^#[0-9a-fA-F]{6}$/.test(local);
  const current = (value ?? '').toLowerCase();

  return (
    <div>
      <span className="text-xs font-medium text-slate-700">{label}</span>

      {/* Quick-pick palette — one click and you're done */}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {COLOR_SWATCHES.map((c) => {
          const active = current === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              onClick={() => {
                setLocal(c);
                onChange(c);
              }}
              className={cn(
                'h-6 w-6 rounded-md border transition relative',
                active
                  ? 'border-brand-500 ring-2 ring-brand-200'
                  : 'border-slate-200 hover:border-slate-400',
                c === '#ffffff' && 'border-slate-300',
              )}
              style={{ background: c }}
              title={c}
              aria-label={`Usar ${c}`}
            >
              {active && (
                <Check
                  size={11}
                  className={cn(
                    'absolute inset-0 m-auto',
                    c === '#ffffff' || c === '#fafafa' ? 'text-slate-700' : 'text-white',
                  )}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Advanced: hex input + native color picker */}
      <details className="mt-2 group">
        <summary className="text-[10px] text-slate-400 cursor-pointer select-none hover:text-slate-600 list-none [&::-webkit-details-marker]:hidden">
          <span className="group-open:hidden">Mostrar color personalizado</span>
          <span className="hidden group-open:inline">Ocultar color personalizado</span>
        </summary>
        <div className="mt-1.5 flex items-stretch rounded-lg border border-slate-300 overflow-hidden">
          <input
            type="color"
            value={local || '#016fff'}
            onChange={(e) => {
              setLocal(e.target.value);
              onChange(e.target.value);
            }}
            className="w-10 h-9 cursor-pointer bg-white border-r border-slate-300"
            aria-label={`${label} (selector)`}
          />
          <input
            type="text"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => {
              if (!local) return onChange(null);
              if (valid) onChange(local);
              else setLocal(value ?? '');
            }}
            placeholder="#016fff"
            className="flex-1 px-2 text-xs font-mono bg-white outline-none"
          />
          {local && (
            <button
              type="button"
              onClick={() => {
                setLocal('');
                onChange(null);
              }}
              className="px-2 text-slate-400 hover:text-slate-700 text-xs"
              aria-label="Quitar"
            >
              ×
            </button>
          )}
        </div>
      </details>
    </div>
  );
}

/* ── Featured Banner ───────────────────────────────────── */

const FEATURED_IMAGE_MAX_MB = 8;

function FeaturedImageUploader({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = `featured-upload-${page.id}`;

  async function handleFile(file: File) {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Sube una imagen (PNG, JPG, WEBP, GIF).');
      return;
    }
    if (file.size > FEATURED_IMAGE_MAX_MB * 1024 * 1024) {
      setError(`La imagen supera ${FEATURED_IMAGE_MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      // Presign — backend stores the object in S3 under bio/featured/{pageId}/
      // and does NOT register it in the media table on purpose, so the user
      // can't wipe it out by mistake from the media library.
      const presign = await api<{
        uploadUrl: string;
        publicUrl: string;
        headers: Record<string, string>;
      }>(`/api/bio/pages/${page.id}/featured-image-upload-url`, {
        method: 'POST',
        body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size }),
      });

      const put = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: presign.headers,
        body: file,
      });
      if (!put.ok) throw new Error('Upload failed');

      // Persist the public URL on the page row.
      onSave({ featuredImageUrl: presign.publicUrl });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  }

  function clear() {
    setError(null);
    onSave({ featuredImageUrl: null });
  }

  return (
    <div>
      <span className="text-xs font-medium text-slate-700">Imagen del banner (opcional)</span>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="relative shrink-0">
          {page.featuredImageUrl ? (
            <img
              src={page.featuredImageUrl}
              alt="Imagen del banner"
              className="h-16 w-16 rounded-lg object-cover border border-slate-200"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
              <Sparkles size={20} />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 rounded-lg bg-white/70 flex items-center justify-center">
              <Loader2 size={18} className="animate-spin text-brand-600" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={inputId}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition w-fit',
              uploading
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50',
            )}
          >
            <Upload size={12} />
            {page.featuredImageUrl ? 'Cambiar imagen' : 'Subir imagen'}
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
            className="hidden"
          />
          {page.featuredImageUrl && !uploading && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-rose-600 w-fit"
            >
              <X size={11} /> Quitar
            </button>
          )}
          <p className="text-[10px] text-slate-400">
            PNG, JPG, WEBP o GIF · máx. {FEATURED_IMAGE_MAX_MB} MB
          </p>
        </div>
      </div>
      {error && (
        <p className="mt-2 text-xs text-rose-600 inline-flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

function FeaturedSection({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  const [t, setT] = useState(page.featuredTitle ?? '');
  const [s, setS] = useState(page.featuredSubtitle ?? '');
  const [url, setUrl] = useState(page.featuredUrl ?? '');

  // Title / subtitle / link auto-save (image is saved instantly on upload).
  useEffect(() => {
    const id = setTimeout(() => {
      const patch: PatchablePage = {};
      if (t !== (page.featuredTitle ?? '')) patch.featuredTitle = t || null;
      if (s !== (page.featuredSubtitle ?? '')) patch.featuredSubtitle = s || null;
      if (url !== (page.featuredUrl ?? '')) patch.featuredUrl = url || null;
      if (Object.keys(patch).length) onSave(patch);
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, s, url]);

  return (
    <EditorSection
      icon={Sparkles}
      title="Banner destacado"
      description="Resalta tu contenido más importante encima de los enlaces."
      badge={
        <Toggle
          checked={page.featuredEnabled}
          onChange={(v) => onSave({ featuredEnabled: v })}
          label={page.featuredEnabled ? 'Activo' : 'Desactivado'}
        />
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <FeaturedImageUploader page={page} onSave={onSave} />
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Título</span>
          <input
            value={t}
            onChange={(e) => setT(e.target.value)}
            placeholder="Nuevo curso disponible"
            maxLength={100}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Subtítulo</span>
          <input
            value={s}
            onChange={(e) => setS(e.target.value)}
            placeholder="Aprende en 30 días"
            maxLength={200}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Enlace al hacer clic (opcional)</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
      </div>
    </EditorSection>
  );
}

/* ── Email Capture / Mailchimp ─────────────────────────── */

function CaptureSection({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  const [title, setTitle] = useState(page.emailCaptureTitle);
  const [button, setButton] = useState(page.emailCaptureButton);
  const [success, setSuccess] = useState(page.emailCaptureSuccess);
  const [apiKey, setApiKey] = useState(page.mailchimpApiKey ?? '');
  const [audience, setAudience] = useState(page.mailchimpAudienceId ?? '');
  const [subs, setSubs] = useState<BioSubscriber[] | null>(null);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      const patch: PatchablePage = {};
      if (title !== page.emailCaptureTitle) patch.emailCaptureTitle = title;
      if (button !== page.emailCaptureButton) patch.emailCaptureButton = button;
      if (success !== page.emailCaptureSuccess) patch.emailCaptureSuccess = success;
      if (apiKey !== (page.mailchimpApiKey ?? '')) patch.mailchimpApiKey = apiKey || null;
      if (audience !== (page.mailchimpAudienceId ?? ''))
        patch.mailchimpAudienceId = audience || null;
      if (Object.keys(patch).length) onSave(patch);
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, button, success, apiKey, audience]);

  async function loadSubs() {
    setLoadingSubs(true);
    try {
      const r = await api<{ subscribers: BioSubscriber[] }>(
        `/api/bio/pages/${page.id}/subscribers`,
      );
      setSubs(r.subscribers);
    } finally {
      setLoadingSubs(false);
    }
  }

  return (
    <EditorSection
      icon={Mail}
      title="Captura de emails"
      description="Recoge suscriptores desde tu bio. Sincronización opcional con Mailchimp."
      badge={
        <Toggle
          checked={page.emailCaptureEnabled}
          onChange={(v) => onSave({ emailCaptureEnabled: v })}
          label={page.emailCaptureEnabled ? 'Activo' : 'Desactivado'}
        />
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Encabezado del formulario</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Texto del botón</span>
          <input
            value={button}
            onChange={(e) => setButton(e.target.value)}
            maxLength={40}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Mensaje de éxito</span>
          <input
            value={success}
            onChange={(e) => setSuccess(e.target.value)}
            maxLength={200}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </label>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <h3 className="text-xs font-semibold text-slate-900 mb-2">Mailchimp (opcional)</h3>
        <p className="text-xs text-slate-500 mb-3">
          Si rellenas estos campos, cada suscriptor se enviará también a tu lista de Mailchimp.
        </p>
        <div className="grid grid-cols-1 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-700">API Key</span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us21"
              className="mt-1.5 w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Audience / List ID</span>
            <input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="abc123def4"
              className="mt-1.5 w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            />
          </label>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-slate-900">Suscriptores capturados</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadSubs}
              className="text-xs text-brand-600 hover:underline"
            >
              {subs === null ? 'Ver lista' : loadingSubs ? 'Cargando…' : 'Recargar'}
            </button>
            <a
              href={`/api/bio/pages/${page.id}/subscribers/export.csv`}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
            >
              <Download size={12} /> CSV
            </a>
          </div>
        </div>
        {subs && (
          <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-slate-200">
            {subs.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500 text-center">
                Aún no hay suscriptores.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {subs.map((s) => (
                  <li key={s.id} className="px-3 py-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-900 truncate">{s.email}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {new Date(s.createdAt).toLocaleDateString()}
                      {s.syncedToMailchimpAt && ' · sincronizado'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </EditorSection>
  );
}

/* ── Custom SEO ────────────────────────────────────────── */

function SeoSection({ page, onSave }: { page: BioPage; onSave: (p: PatchablePage) => void }) {
  const [t, setT] = useState(page.seoTitle ?? '');
  const [d, setD] = useState(page.seoDescription ?? '');
  const [img, setImg] = useState(page.seoImageUrl ?? '');

  useEffect(() => {
    const id = setTimeout(() => {
      const patch: PatchablePage = {};
      if (t !== (page.seoTitle ?? '')) patch.seoTitle = t || null;
      if (d !== (page.seoDescription ?? '')) patch.seoDescription = d || null;
      if (img !== (page.seoImageUrl ?? '')) patch.seoImageUrl = img || null;
      if (Object.keys(patch).length) onSave(patch);
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, d, img]);

  return (
    <EditorSection
      icon={Search}
      title="SEO personalizado"
      description="Cómo se ve tu página en Google y al compartir en redes sociales."
    >
      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Título SEO</span>
          <input
            value={t}
            onChange={(e) => setT(e.target.value)}
            placeholder={`${page.title || `@${page.slug}`} · zlink.club`}
            maxLength={120}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
          <span className="text-[10px] text-slate-400">{t.length}/120 (recomendado &lt; 60)</span>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Descripción</span>
          <textarea
            value={d}
            onChange={(e) => setD(e.target.value)}
            placeholder="Una frase que describa tu página para Google y al compartir."
            maxLength={250}
            rows={3}
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none resize-none"
          />
          <span className="text-[10px] text-slate-400">{d.length}/250 (recomendado &lt; 160)</span>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Imagen para compartir (OG)</span>
          <input
            value={img}
            onChange={(e) => setImg(e.target.value)}
            placeholder="https://…"
            className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
          <span className="text-[10px] text-slate-400">
            Recomendado 1200×630. Si lo dejas vacío usamos tu avatar.
          </span>
        </label>
      </div>
    </EditorSection>
  );
}

/* ── Analytics ─────────────────────────────────────────── */

function AnalyticsSection({
  page,
  onSave,
}: {
  page: BioPage;
  onSave: (p: PatchablePage) => void;
}) {
  const [ga, setGa] = useState(page.gaMeasurementId ?? '');
  const [meta, setMeta] = useState(page.metaPixelId ?? '');
  const [tiktok, setTiktok] = useState(page.tiktokPixelId ?? '');
  const [data, setData] = useState<BioAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [gaError, setGaError] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [tiktokError, setTiktokError] = useState<string | null>(null);

  // Save GA id on blur.
  function commitGa() {
    const trimmed = ga.trim();
    if (trimmed === (page.gaMeasurementId ?? '')) return;
    if (!trimmed) {
      setGaError(null);
      onSave({ gaMeasurementId: null });
      return;
    }
    if (!/^(G-[A-Z0-9]+|UA-[0-9]+-[0-9]+)$/i.test(trimmed)) {
      setGaError('Formato inválido (debe empezar por G- o UA-).');
      return;
    }
    setGaError(null);
    onSave({ gaMeasurementId: trimmed });
  }

  // Save Meta (Facebook) Pixel id on blur — numeric id (usually 15-16 digits).
  function commitMeta() {
    const trimmed = meta.trim();
    if (trimmed === (page.metaPixelId ?? '')) return;
    if (!trimmed) {
      setMetaError(null);
      onSave({ metaPixelId: null });
      return;
    }
    if (!/^[0-9]{6,20}$/.test(trimmed)) {
      setMetaError('El ID del Pixel de Meta son solo números (p. ej. 123456789012345).');
      return;
    }
    setMetaError(null);
    onSave({ metaPixelId: trimmed });
  }

  // Save TikTok Pixel id on blur — alphanumeric (p. ej. C4A1B2...).
  function commitTiktok() {
    const trimmed = tiktok.trim();
    if (trimmed === (page.tiktokPixelId ?? '')) return;
    if (!trimmed) {
      setTiktokError(null);
      onSave({ tiktokPixelId: null });
      return;
    }
    if (!/^[A-Z0-9]{8,40}$/i.test(trimmed)) {
      setTiktokError('ID de Pixel de TikTok inválido (solo letras y números).');
      return;
    }
    setTiktokError(null);
    onSave({ tiktokPixelId: trimmed });
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api<BioAnalytics>(`/api/bio/pages/${page.id}/analytics`);
      setData(r);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <EditorSection
      icon={BarChart3}
      title="Analítica"
      description="Visitas, clics y suscriptores. Conecta GA4 si quieres más detalle."
      defaultOpen
    >
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Visitas" value={data?.views ?? page.views} loading={loading} />
        <StatCard label="Clics totales" value={data?.totalClicks ?? 0} loading={loading} />
        <StatCard
          label="CTR"
          value={data ? `${(data.ctr * 100).toFixed(1)}%` : '—'}
          loading={loading}
        />
        <StatCard label="Suscriptores" value={data?.subscribers ?? 0} loading={loading} />
      </div>

      {/* Per-link clicks */}
      {data && data.links.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs font-semibold text-slate-900 mb-2">Clics por enlace</h3>
          <ul className="space-y-1.5">
            {data.links.map((l) => {
              const max = Math.max(1, ...data.links.map((x) => x.clicks));
              const pct = (l.clicks / max) * 100;
              return (
                <li key={l.id} className="text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-slate-700">{l.title}</span>
                    <span className="font-semibold text-slate-900 shrink-0">{l.clicks}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        l.active ? 'bg-brand-500' : 'bg-slate-300',
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* GA4 connect */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <h3 className="text-xs font-semibold text-slate-900 mb-2">Google Analytics 4</h3>
        <p className="text-xs text-slate-500 mb-3">
          Pega tu Measurement ID (empieza por <code className="font-mono">G-</code>) y lo
          inyectaremos en tu página pública.
        </p>
        <div className="flex gap-2">
          <input
            value={ga}
            onChange={(e) => setGa(e.target.value)}
            onBlur={commitGa}
            placeholder="G-XXXXXXXXXX"
            className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </div>
        {gaError && <p className="mt-1 text-xs text-rose-600">{gaError}</p>}
      </div>

      {/* Meta (Facebook) Pixel */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <h3 className="text-xs font-semibold text-slate-900 mb-2">Pixel de Meta (Facebook)</h3>
        <p className="text-xs text-slate-500 mb-3">
          Pega el <strong>ID del Pixel</strong> (solo números) y lo inyectaremos en tu página pública.
        </p>
        <input
          value={meta}
          onChange={(e) => setMeta(e.target.value)}
          onBlur={commitMeta}
          placeholder="123456789012345"
          className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
        />
        {metaError && <p className="mt-1 text-xs text-rose-600">{metaError}</p>}
      </div>

      {/* TikTok Pixel */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <h3 className="text-xs font-semibold text-slate-900 mb-2">Pixel de TikTok</h3>
        <p className="text-xs text-slate-500 mb-3">
          Pega el <strong>ID del Pixel</strong> de TikTok Ads y lo inyectaremos en tu página pública.
        </p>
        <input
          value={tiktok}
          onChange={(e) => setTiktok(e.target.value)}
          onBlur={commitTiktok}
          placeholder="C4XXXXXXXXXXXXXXXX"
          className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
        />
        {tiktokError && <p className="mt-1 text-xs text-rose-600">{tiktokError}</p>}
      </div>
    </EditorSection>
  );
}

function StatCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | string;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className="text-lg font-bold text-slate-900 mt-0.5">
        {loading ? <span className="text-slate-300">—</span> : value}
      </p>
    </div>
  );
}

/* ── Links section ─────────────────────────────────────── */

function LinksSection({ links, pageId }: { links: BioLink[]; pageId: string }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  function patchCache(updater: (links: BioLink[]) => BioLink[]) {
    qc.setQueryData<BioResponse>(['bio', 'page', pageId], (prev) =>
      prev ? { ...prev, links: updater(prev.links) } : prev,
    );
  }

  const createLink = useMutation({
    mutationFn: (input: Pick<BioLink, 'title' | 'url' | 'iconValue'>) =>
      api<{ link: BioLink }>(`/api/bio/pages/${pageId}/links`, {
        method: 'POST',
        body: JSON.stringify({ ...input, iconType: 'emoji' }),
      }),
    onSuccess: (res) => {
      patchCache((ls) => [...ls, res.link]);
      setAdding(false);
    },
  });

  const updateLink = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<BioLink> }) =>
      api<{ link: BioLink }>(`/api/bio/links/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onSuccess: (res) => {
      patchCache((ls) => ls.map((l) => (l.id === res.link.id ? res.link : l)));
      setEditingId(null);
    },
  });

  const deleteLink = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/bio/links/${id}`, { method: 'DELETE' }),
    onSuccess: (_res, id) => {
      patchCache((ls) => ls.filter((l) => l.id !== id));
    },
  });

  const reorderLinks = useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      api(`/api/bio/pages/${pageId}/links/reorder`, {
        method: 'POST',
        body: JSON.stringify({ order }),
      }),
  });

  function commitOrder(next: BioLink[]) {
    const remapped = next.map((l, i) => ({ ...l, sortOrder: i + 1 }));
    patchCache(() => remapped);
    reorderLinks.mutate(remapped.map((l) => ({ id: l.id, sortOrder: l.sortOrder })));
  }

  function move(id: string, dir: -1 | 1) {
    const idx = links.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= links.length) return;
    const next = [...links];
    const tmp = next[idx]!;
    next[idx] = next[swap]!;
    next[swap] = tmp;
    commitOrder(next);
  }

  // Drag & drop: move the dragged link to the drop target's position.
  function reorderTo(fromId: string, toId: string) {
    if (fromId === toId) return;
    const from = links.findIndex((l) => l.id === fromId);
    const to = links.findIndex((l) => l.id === toId);
    if (from < 0 || to < 0) return;
    const next = [...links];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    commitOrder(next);
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-900">Enlaces</h2>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 transition"
        >
          <Plus size={13} /> Añadir enlace
        </button>
      </div>

      {adding && (
        <LinkForm
          onCancel={() => setAdding(false)}
          onSubmit={(values) => createLink.mutate(values)}
          submitting={createLink.isPending}
        />
      )}

      {links.length === 0 && !adding ? (
        <p className="py-8 text-center text-sm text-slate-400">
          Aún no has añadido enlaces. Empieza por tu web, tu Instagram o tu tienda.
        </p>
      ) : (
        <ul className="space-y-2">
          {links.map((link, i) => (
            <li key={link.id}>
              {editingId === link.id ? (
                <LinkForm
                  defaultValues={link}
                  onCancel={() => setEditingId(null)}
                  onSubmit={(values) => updateLink.mutate({ id: link.id, patch: values })}
                  submitting={updateLink.isPending}
                />
              ) : (
                <div
                  draggable
                  onDragStart={(e) => {
                    setDragId(link.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', link.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragId && overId !== link.id) setOverId(link.id);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId) reorderTo(dragId, link.id);
                    setDragId(null);
                    setOverId(null);
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2.5 transition',
                    link.active
                      ? 'border-slate-200 bg-white hover:border-slate-300'
                      : 'border-slate-200 bg-slate-50',
                    dragId === link.id ? 'opacity-40' : link.active ? '' : 'opacity-60',
                    overId === link.id && dragId && dragId !== link.id && 'border-brand-400 ring-2 ring-brand-200',
                  )}
                >
                  <span
                    className="shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
                    aria-hidden
                    title="Arrastra para reordenar"
                  >
                    <GripVertical size={14} />
                  </span>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => move(link.id, -1)}
                      disabled={i === 0}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Mover arriba"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(link.id, 1)}
                      disabled={i === links.length - 1}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Mover abajo"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <span className="text-xl" aria-hidden>
                    {link.iconValue}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{link.title}</p>
                    <p className="text-xs text-slate-500 truncate">{link.url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updateLink.mutate({ id: link.id, patch: { active: !link.active } })
                    }
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label={link.active ? 'Ocultar enlace' : 'Mostrar enlace'}
                    title={link.active ? 'Ocultar' : 'Mostrar'}
                  >
                    {link.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(link.id)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Editar"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('¿Eliminar este enlace?')) deleteLink.mutate(link.id);
                    }}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    aria-label="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Link form ─────────────────────────────────────────── */

const EMOJI_PRESETS = ['🔗', '🌐', '🛒', '🎟️', '📸', '🎥', '🎵', '✉️', '📝', '💼', '🎯', '⭐', '❤️'];

function LinkForm({
  defaultValues,
  onCancel,
  onSubmit,
  submitting,
}: {
  defaultValues?: Pick<BioLink, 'title' | 'url' | 'iconValue'>;
  onCancel: () => void;
  onSubmit: (v: { title: string; url: string; iconValue: string }) => void;
  submitting: boolean;
}) {
  const [title, setTitle] = useState(defaultValues?.title ?? '');
  const [url, setUrl] = useState(defaultValues?.url ?? '');
  const [iconValue, setIconValue] = useState(defaultValues?.iconValue ?? '🔗');

  const valid = title.trim().length > 0 && /^https?:\/\//.test(url.trim());

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3 mb-3">
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <select
          value={iconValue}
          onChange={(e) => setIconValue(e.target.value)}
          className="w-12 h-10 text-lg text-center bg-white border border-slate-200 rounded-md"
        >
          {EMOJI_PRESETS.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título (p. ej. Mi tienda)"
          maxLength={80}
          className="h-10 px-3 text-sm rounded-md border border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
        />
      </div>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…"
        className="mt-2 h-10 w-full px-3 text-sm rounded-md border border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
      />
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!valid || submitting}
          onClick={() => onSubmit({ title: title.trim(), url: url.trim(), iconValue })}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Guardar
        </button>
      </div>
    </div>
  );
}

/* ── Live preview ──────────────────────────────────────── */

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const FONT_FAMILIES: Record<string, string> = {
  system:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
  inter: '"Inter","Helvetica Neue",Arial,sans-serif',
  serif: 'Georgia,"Times New Roman",serif',
  mono: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
  rounded: '"SF Pro Rounded","Nunito",-apple-system,sans-serif',
};

/**
 * Returns true when a hex color is "dark" enough that white text reads better
 * on top of it than black. Uses HSP (perceived) luminance.
 */
function isDarkHex(hex: string): boolean {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const L = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b);
  return L < 140; // tuned threshold
}

/** Pick a readable text color on top of `bg` (hex). */
function autoContrastingText(bg: string): string {
  return isDarkHex(bg) ? '#ffffff' : '#0f172a';
}

function previewVars(page: BioPage): {
  bg: string;
  fg: string;
  muted: string;
  btnBg: string;
  btnFg: string;
  btnRadius: string;
  ff: string;
} {
  // Defaults by theme
  let bg = '#fafafa';
  let fg = '#0f172a';
  let muted = '#64748b';
  let btnBg = '#f1f5f9';
  if (page.theme === 'dark') {
    bg = '#0b1220';
    fg = '#ffffff';
    muted = 'rgba(255,255,255,.7)';
    btnBg = 'rgba(255,255,255,.10)';
  } else if (page.theme === 'brand') {
    const c = page.brandColor && HEX_RE.test(page.brandColor) ? page.brandColor : '#016fff';
    bg = c;
    fg = '#ffffff';
    muted = 'rgba(255,255,255,.85)';
    btnBg = 'rgba(255,255,255,.18)';
  }
  // Background overrides
  if (page.backgroundType === 'solid' && page.backgroundValue && HEX_RE.test(page.backgroundValue)) {
    bg = page.backgroundValue;
  } else if (page.backgroundType === 'gradient' && page.backgroundValue) {
    bg = page.backgroundValue;
  }
  // Text + button overrides
  if (page.textColor && HEX_RE.test(page.textColor)) fg = page.textColor;
  if (page.buttonColor && HEX_RE.test(page.buttonColor)) btnBg = page.buttonColor;
  /**
   * Button text color rules:
   * - For `outline` style the picked `buttonTextColor` controls border+text and
   *   we respect it as-is (the button background is transparent so contrast is
   *   against the page bg).
   * - For every other style we auto-pick a contrasting text color on top of
   *   `btnBg` so a dark-on-dark combination is impossible. We still let the
   *   user override with `buttonTextColor` when it has enough contrast.
   */
  let btnFg: string;
  if (page.buttonStyle === 'outline') {
    btnFg = page.buttonTextColor && HEX_RE.test(page.buttonTextColor)
      ? page.buttonTextColor
      : btnBg;
  } else if (HEX_RE.test(btnBg)) {
    const userPick = page.buttonTextColor && HEX_RE.test(page.buttonTextColor)
      ? page.buttonTextColor
      : null;
    const auto = autoContrastingText(btnBg);
    if (userPick && isDarkHex(userPick) !== isDarkHex(btnBg)) {
      // User pick has good contrast (one is dark, the other light) → keep it.
      btnFg = userPick;
    } else {
      btnFg = auto;
    }
  } else {
    btnFg = page.buttonTextColor && HEX_RE.test(page.buttonTextColor) ? page.buttonTextColor : fg;
  }
  const btnRadius = page.buttonStyle === 'pill' ? '999px' : '12px';
  const ff = FONT_FAMILIES[page.fontFamily ?? 'system'] ?? FONT_FAMILIES.system!;
  return { bg, fg, muted, btnBg, btnFg, btnRadius, ff };
}

function Preview({ page, links }: { page: BioPage; links: BioLink[] }) {
  const v = previewVars(page);
  const styleKey = page.buttonStyle;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 text-center">
        Vista previa
      </p>
      {/*
        Inject the same per-style hover rules that the public SSR page uses, so
        the preview faithfully shows how a click/hover will look. We scope
        every selector to .bio-preview-frame so the CSS cannot leak outside
        the phone mockup.
      */}
      <style>{`
        .bio-preview-frame .bio-lnk{
          --btn-bg:${v.btnBg};
          --btn-fg:${v.btnFg};
          background:var(--btn-bg);
          color:var(--btn-fg);
          border-radius:${v.btnRadius};
          transition:filter .15s ease, transform .15s ease, background .15s ease, color .15s ease;
        }
        .bio-preview-frame .bio-lnk-outline{
          background:transparent;
          color:var(--btn-bg);
          border:1px solid var(--btn-bg);
        }
        .bio-preview-frame .bio-lnk-soft{
          background:color-mix(in srgb, var(--btn-bg) 22%, transparent);
          color:var(--btn-bg);
        }
        .bio-preview-frame .bio-lnk:hover{filter:brightness(.94);transform:translateY(-1px)}
        .bio-preview-frame .bio-lnk-outline:hover{background:var(--btn-bg);color:var(--btn-fg);filter:none;transform:translateY(-1px)}
        .bio-preview-frame .bio-lnk-soft:hover{background:color-mix(in srgb, var(--btn-bg) 32%, transparent);filter:none;transform:translateY(-1px)}
      `}</style>
      <div
        className="bio-preview-frame mx-auto rounded-[28px] border-8 overflow-hidden h-[520px] w-[260px] relative border-slate-200"
        style={{ background: v.bg, fontFamily: v.ff }}
      >
        <div className="absolute inset-0 overflow-y-auto px-5 py-6 text-center">
          {page.avatarUrl ? (
            <img
              src={page.avatarUrl}
              alt=""
              className="mx-auto h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="mx-auto h-16 w-16 rounded-full bg-white/20" />
          )}
          <h3 className="mt-3 font-bold text-base" style={{ color: v.fg }}>
            {page.title || `@${page.slug}`}
          </h3>
          {page.bio && (
            <p className="mt-1 text-xs leading-snug" style={{ color: v.muted }}>
              {page.bio}
            </p>
          )}

          {/* Social media row — subtle tinted circle behind each glyph,
              derived from the body text color so it stays visible on every
              theme (including presets where buttonColor and buttonTextColor
              are the same hex, like Papel). */}
          {(() => {
            const items = SOCIAL_NETWORKS.filter((n) => {
              const url = page.socials?.[n.id];
              return typeof url === 'string' && url.trim().length > 0;
            });
            if (items.length === 0) return null;
            return (
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                {items.map((n) => (
                  <span
                    key={n.id}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                    style={{
                      background: `color-mix(in srgb, ${v.fg} 12%, transparent)`,
                      color: v.fg,
                    }}
                  >
                    <SocialIcon svg={n.svg} size={14} />
                  </span>
                ))}
              </div>
            );
          })()}

          {/* Featured banner */}
          {page.featuredEnabled && (page.featuredTitle || page.featuredImageUrl) && (
            <div
              className="mt-4 flex items-center gap-2 rounded-xl px-2.5 py-2 text-left"
              style={{ background: v.btnBg, color: v.btnFg }}
            >
              {page.featuredImageUrl ? (
                <img
                  src={page.featuredImageUrl}
                  alt=""
                  className="h-10 w-10 rounded-md object-cover shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded-md bg-white/30 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                {page.featuredTitle && (
                  <div className="text-[11px] font-bold leading-tight truncate">
                    {page.featuredTitle}
                  </div>
                )}
                {page.featuredSubtitle && (
                  <div className="text-[10px] opacity-80 leading-tight truncate">
                    {page.featuredSubtitle}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-5 space-y-2">
            {links.length === 0 ? (
              <p className="text-[11px] italic" style={{ color: v.muted }}>
                Añade tu primer enlace →
              </p>
            ) : (
              links.map((l) => {
                const cls =
                  styleKey === 'outline'
                    ? 'bio-lnk bio-lnk-outline'
                    : styleKey === 'soft'
                      ? 'bio-lnk bio-lnk-soft'
                      : 'bio-lnk';
                return (
                  <button
                    type="button"
                    key={l.id}
                    className={cn(
                      cls,
                      'w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-semibold cursor-pointer',
                    )}
                  >
                    <span className="text-base shrink-0">{l.iconValue}</span>
                    <span className="truncate flex-1">{l.title}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Email capture */}
          {page.emailCaptureEnabled && (
            <div
              className="mt-4 rounded-xl px-3 py-3 text-left"
              style={{ background: v.btnBg, color: v.btnFg }}
            >
              <div className="text-[11px] font-bold text-center mb-2">{page.emailCaptureTitle}</div>
              <div className="flex gap-1.5">
                <div className="flex-1 px-2 py-1.5 rounded-md bg-white/80 text-[10px] text-slate-500">
                  tu@email.com
                </div>
                <div
                  className="px-2 py-1.5 rounded-md text-[10px] font-semibold"
                  style={{ background: v.btnBg, color: v.btnFg, border: '1px solid currentColor' }}
                >
                  {page.emailCaptureButton}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
